import type { ProductListItem } from '@providers/ProductListProvider';
import { useMemo } from 'react';

export const useProduct = (
  product: ProductListItem,
  noImageUrl?: string,
  selectedVariantImage?: string
) => {
  const labelByTag = useMemo(() => {
    const tags = [...(product.tags || []), ...(product.original_tags || [])];

    return tags
      .filter((item) => item.indexOf('pfs:label') !== -1)
      .map((item) => item.split('pfs:label-')[1]);
  }, [product]);

  const isSoldOut = !product.available;

  const previewProductImages = useMemo(() => {
    const productInfo = product.images_info;

    if (!productInfo.length) {
      return [noImageUrl || ''];
    }

    // If we have a selected variant image from filter, prioritize it
    if (selectedVariantImage) {
      // Use selected variant image as primary, avoid duplicates with fallback
      const fallbackImage =
        productInfo.length > 1 && productInfo[1].src !== selectedVariantImage
          ? productInfo[1].src
          : productInfo[0].src !== selectedVariantImage
          ? productInfo[0].src
          : undefined;

      return fallbackImage ? [selectedVariantImage, fallbackImage] : [selectedVariantImage];
    }

    // For split products (variant display), try to use the specific variant's image first
    if (product.variant_id != null && product.variants?.length) {
      const currentVariant = product.variants.find((variant) => {
        // Handle string comparison for robustness (both types are ultimately strings in data)
        return variant.id.toString() === product.variant_id?.toString();
      });

      if (currentVariant?.image) {
        // Use variant image as primary, avoid duplicates with fallback
        const fallbackImage =
          productInfo.length > 1 && productInfo[1].src !== currentVariant.image
            ? productInfo[1].src
            : productInfo[0].src !== currentVariant.image
            ? productInfo[0].src
            : undefined;

        return fallbackImage ? [currentVariant.image, fallbackImage] : [currentVariant.image];
      }
    }

    // Default behavior - use first two images
    if (productInfo.length === 1) {
      return [productInfo[0].src];
    }

    return [productInfo[0].src, productInfo[1].src];
  }, [product, selectedVariantImage, noImageUrl]);

  const isSale = useMemo(() => {
    return (product.compare_at_price_min || 0) > product.price_min;
  }, [product]);

  const salePercent = useMemo(() => {
    return isSale && product.compare_at_price_min
      ? Math.round(
          (((product.compare_at_price_min || 0) - product.price_min) * 100) /
            product.compare_at_price_min
        ) + '%'
      : undefined;
  }, [product]);

  const saleAmount = useMemo(() => {
    return isSale ? (product.compare_at_price_min || 0) - product.price_min : undefined;
  }, [product]);

  const firstImage = product.images_info.length > 0 ? product.images_info[0] : undefined;

  return {
    firstImage,
    labelByTag,
    isSoldOut,
    isSale,
    salePercent,
    saleAmount,
    previewProductImages,
  };
};

export default useProduct;
